import { useCallback, useRef, useState } from "react";

import { AMAZON_SEARCH_LINK_WIDTH } from "@/components/shared/AmazonSearchLink";

// AUT-143/144: bounds for the sticky column's keyword segment, and the width
// of the fixed segments ahead of it (checkbox + star, see stickyGrid in
// KeywordBankTable/index.tsx and ThemeGroupedView) that both keyword tables
// share -- kept in one place instead of duplicated per table.
const MIN_KEYWORD_COL = { readOnly: 160, editable: 200 };
const DEFAULT_KEYWORD_COL = { readOnly: 208, editable: 248 };
const MAX_KEYWORD_COL = 560;
// AUT-496: the Amazon search icon is a fixed segment *after* the keyword text, so it comes out of
// the text's share the same way the checkbox and star do. The column is drag-resizable, so a
// per-row guess goes wrong at the minimum width; the width comes from the component itself.
const FIXED_SEGMENTS_WIDTH = {
  readOnly: 28 + AMAZON_SEARCH_LINK_WIDTH,
  editable: 40 + 28 + AMAZON_SEARCH_LINK_WIDTH,
};

/**
 * Drag-to-resize for the keyword column shared by KeywordBankTable and
 * ThemeGroupedView. Hand-rolled rather than pulling in a table library --
 * neither table is built on one, and a single column's resize is just
 * pointer-capture + clamped width state.
 */
export function useKeywordColumnResize(readOnly: boolean) {
  const [width, setWidth] = useState(
    readOnly ? DEFAULT_KEYWORD_COL.readOnly : DEFAULT_KEYWORD_COL.editable,
  );
  const drag = useRef<{ startX: number; startWidth: number } | null>(null);
  const min = readOnly ? MIN_KEYWORD_COL.readOnly : MIN_KEYWORD_COL.editable;

  const onResizeStart = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      drag.current = { startX: e.clientX, startWidth: width };
      document.body.style.userSelect = "none";
      document.body.style.cursor = "col-resize";
    },
    [width],
  );

  const onResizeMove = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      if (!drag.current) return;
      const delta = e.clientX - drag.current.startX;
      setWidth(Math.min(MAX_KEYWORD_COL, Math.max(min, drag.current.startWidth + delta)));
    },
    [min],
  );

  const onResizeEnd = useCallback((e: React.PointerEvent<HTMLElement>) => {
    if (!drag.current) return;
    drag.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
  }, []);

  const fixedSegmentsWidth = readOnly ? FIXED_SEGMENTS_WIDTH.readOnly : FIXED_SEGMENTS_WIDTH.editable;

  return {
    stickyStyle: { width, minWidth: width },
    keywordTextWidth: width - fixedSegmentsWidth,
    onResizeStart,
    onResizeMove,
    onResizeEnd,
  };
}
